import { purple } from "@mui/material/colors";
import { createTheme } from "@mui/material/styles";

// #E9EAF0

export const theme = createTheme({
  cssVariables: true,
  colorSchemeSelector: "data-theme",
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      wide: 1700,
    },
  },
  shape: {
    borderRadius: 3,
  },

  typography: {
    fontFamily: `"IBM Plex Sans", "Inter","Roboto","Helvetica","Arial", "LexFont", sans-serif`,

    h1: {
      fontFamily: `"LexFont", "IBM Plex Sans", sans-serif`,
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontFamily: `"LexFont", "IBM Plex Sans", sans-serif`,
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h3: {
      fontFamily: `"LexFont", "IBM Plex Sans", sans-serif`,
      fontSize: "2rem",
      fontWeight: 400,
      letterSpacing: "-0.01em",
    },
    h6: {
      fontFamily: `"IBM Plex Sans", "Inter","Roboto", sans-serif`,
      fontSize: "1.1rem",
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },

    body1: { fontWeight: 400, letterSpacing: "-0.01em" },

    button: {
      textTransform: "none",
      fontWeight: 600,
      letterSpacing: "0.02em",
    },
  },

  colorSchemes: {
    light: {
      palette: {
        mode: "light",

        primary: {
          main: "#DBFE6B",
          mainDark: "#ABD200",
          contrastText: "#3F3F45",
        },

        secondary: {
          main: "#6C63FF",
          contrastText: "#F3F3F3",
        },

        background: {
          default: "#e7e7eb",
          paper: "#e7e7eb",
          tinted: "rgba(233, 234, 240, 0.6)",
          greyedOut: "#C1C4C6",
        },

        text: {
          primary: "#3F3F45",
          secondary: "#757E95",
          accent: "#6C63FF",
        },
      },
    },

    dark: {
      palette: {
        mode: "dark",

        primary: {
          main: "#DBFE6B",
          contrastText: "#3F3F45",
        },

        secondary: {
          main: "#6E7BFF",
          contrastText: "#F3F3F3",
        },

        background: {
          default: "#3F3F45",
          paper: "#3F3F45",
          tinted: "rgba(63, 63, 69, 0.5)",
          greyedOut: "#C1C4C6",
        },

        text: {
          primary: "#F3F3F3",
          secondary: "#C1C4C6",
          accent: "#DBFE6B"
        },
      },
    },
  },

  custom: {
    surfaces: {
      app: {
        light: "#e7e7eb",
        dark: "#3F3F45",
      },
      panel: {
        light: "#e7e7eb",
        dark: "#3F3F45",
      },
      control: {
        light: "#e7e7eb",
        dark: "#3F3F45",
      },
      inset: {
        light: "#e7e7eb",
        dark: "#3F3F45",
      },
      concave: {
        light: "linear-gradient(145deg, #DBDBDF, #F3F3F7)",
        dark: "linear-gradient(145deg, #383840, #4a4a52)",
      },
      muted: {
        light: "#C1C4C6",
        dark: "#5A5D66",
      },
    },

    elevation: {
      panel: {
        light: "8px 8px 16px #c4c4c8, -8px -8px 16px #ffffff",
        dark: "8px 8px 16px #27272b, -8px -8px 16px #57575f",
      },
      control: {
        light: "2px 2px 5px #c4c4c8, -2px -2px 5px #ffffff",
        dark: "2px 2px 5px #27272b, -2px -2px 5px #57575f",
      },
      inset: {
        light: "inset 5px 5px 10px #c3c8d6, inset -5px -5px 10px #ffffff",
        dark: "inset 5px 5px 17px #27272b, inset -5px -5px 17px #57575f",
      },
      insetSm: {
        light: "inset 3px 3px 6px #c3c8d6, inset -3px -3px 6px #ffffff",
        dark: "inset 3px 3px 8px #27272b, inset -3px -3px 8px #57575f",
      },
      concave: {
        light: "3px 3px 10px #c4c4c8, -3px -3px 10px #ffffff",
        dark: "3px 3px 10px #2b2b30, -3px -3px 10px #565660",
      },
      flat: {
        light: "none",
        dark: "none",
      },
    },

    gradients: {
      accent: "linear-gradient(145deg, #E6FF70 0%, #c7e661 100%)",
      brand: "linear-gradient(145deg, #6C63FF 0%, #3b30d1 100%)",
      accentReverse: "linear-gradient(280deg, #E6FF70 0%, #c7e661 100%)",
      brandReverse: "linear-gradient(280deg, #6C63FF 0%, #3b30d1 100%)",
      teal: "linear-gradient(145deg, #36D6C9 0%, #22a89d 100%)",
      tealReverse: "linear-gradient(280deg, #36D6C9 0%, #22a89d 100%)",
      dev: "linear-gradient(145deg,rgba(34, 193, 195, 1) 0%, rgba(45, 253, 184, 1) 100%)",
      accentSubtle: "linear-gradient(145deg, rgba(219, 254, 107, 0.3) 0%, rgba(225, 247, 103, 0.3) 100%)",
      critical: "linear-gradient(145deg, #ff2f52 0%, #B5004E 100%)",
      criticalReverse: "linear-gradient(280deg, #ff2f52 0%, #B5004E 100%)",
    },

    effects: {
      accentRaised: "2px 2px 5px #DBFE6B, -2px -2px 5px #e1f767",
      accentInset: "inset 5px 5px 10px #DBFE6B, inset -5px -5px 10px #bedd4e",
      limeTint: "rgba(220, 254, 107, 0.4)",
      purpleTint: "rgba(140, 132, 250, 0.4)",
      lightTint: "rgba(188, 188, 227, 0.2)",
      darkTint: "rgba(28, 28, 53, 0.5)",
      criticalTint: "rgba(255, 47, 82, 0.14)",
    },

    borders: {
      subtle: "1px solid rgba(255, 255, 255, 0.15)",
      sharp: "1px solid rgba(116, 114, 114, 0.3)",
      lime: "3px solid #bedd4e",
      purple: "3px solid #6C63FF"
    },

    dividers: {
      taperedLight:
        "linear-gradient(90deg, rgba(219, 254, 107, 0) 0%, rgb(66, 66, 66) 50%, rgba(219, 254, 107, 0) 100%)",
      taperedDark:
        "linear-gradient(90deg, rgba(219, 254, 107, 0) 0%, rgba(219, 254, 107, 1) 50%, rgba(219, 254, 107, 0) 100%)",
      subtleUniversal: "rgba(185, 185, 185, 0.4)",
    },

    semantic: {
      muted: {
        main: "#6B6D78",
        contrast: "#FFFFFF",
      },
      open: {
        main: "#a2db1c",
        contrast: "#292929",
      },
      escalated: {
        main: "#551cdb",
        contrast: "#292929",
      },
      low: {
        main: "#C1C4C6",
        contrast: "#1F2A1F",
      },
      medium: {
        main: "#36D6C9",
        contrast: "#102928",
      },
      high: {
        main: "#6C63FF",
        contrast: "#FFFFFF",
      },
      warning: {
        main: "#FF9F1C",
        contrast: "#102928",
      },
      critical: {
        main: "#ff2f52",
        contrast: "#FFFFFF",
      },
    }
  },

  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          boxShadow: "none",
          transition: "none",
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          boxShadow: "none",
          transition: "none",
        },
      },
    },

    MuiButtonBase: {
      styleOverrides: {
        root: {
          WebkitTapHighlightColor: "transparent",
          transition: "none",
        },
      },
      styleOverrides: {
        root: ({ theme }) => ({
          WebkitTapHighlightColor: "transparent",
          transition: "none",

          "& .MuiTouchRipple-root": {
            color: theme.palette.secondary.main,
            opacity: 0.47,
            ...theme.applyStyles("dark", { color: theme.palette.primary.main }),
          },
        }),
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          boxShadow: "none",
        },
        outlined: ({ theme }) => ({
          color: theme.palette.text.primary,
          borderColor: theme.palette.divider,

          "&:hover": {
            borderColor: theme.palette.primary.main,
            boxShadow: `1px 1px 3px ${theme.palette.text.secondary}`,
            backgroundColor: "transparent"
          },
        }),
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: "none",

          "&:hover": {
            backgroundColor: "transparent",
          },

          "&.Mui-selected": {
            backgroundColor: "transparent",
          },

          "&.Mui-selected:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 48,
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&.Mui-selected": {
            backgroundColor: "transparent",
          },
          "&.Mui-selected:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,

          "&.Mui-selected": {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: {
          shrink: true,
        },
      },
    },

  },
});
